import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useEffect, useCallback, Suspense, Fragment as Fragment$1, lazy } from "react";
import { useInView } from "react-intersection-observer";
import { E as ErrorFallback, a as ErrorBoundaryComponent, F as ForceClientSideRender } from "./ForceClientSideRender-2a49390e.js";
import { CacheProvider, ThemeProvider } from "@emotion/react";
import "react-error-boundary";
const triggerFpti = (payload) => {
  var _a, _b, _c, _d;
  if (typeof window === "undefined" || !window.PAYPAL) {
    return {};
  }
  return (_d = (_c = (_b = (_a = window.PAYPAL) == null ? void 0 : _a.analytics) == null ? void 0 : _b.instance) == null ? void 0 : _c.record) == null ? void 0 : _d.call(_c, { data: { ...window.fpti || {}, ...payload } });
};
const FptiWrapper = ({ children, onVisible }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  useEffect(() => {
    if (inView) {
      onVisible();
    }
  }, [inView, onVisible]);
  return /* @__PURE__ */ jsxs("div", { ref, children: [
    " ",
    children,
    " "
  ] });
};
const getComponentWrapper = (pageContext, ComponentsMap, propsContext) => {
  const { __EXPERIMENTAL__ } = pageContext.additionalContext ?? {};
  if ((__EXPERIMENTAL__ == null ? void 0 : __EXPERIMENTAL__.disableAnimationsAboveTheFold) && ComponentsMap.Placement) {
    const { isAboveTheFold } = propsContext;
    return [ComponentsMap.Placement, { isAboveTheFold }];
  }
  return [Fragment$1, {}];
};
const getComponentContextProps = ({ componentOrderIdx }) => ({
  componentOrderIdx: typeof componentOrderIdx === "number" ? componentOrderIdx : 0
});
const initializeComponentRender = (ComponentsMap, pageContext, options) => {
  const { injectComponentContext = false } = options ?? {};
  const { preview } = pageContext.additionalContext || {};
  const { enabled: isPreviewEnabled } = preview || { enabled: false };
  return (componentData, additionalData) => {
    const { isAboveTheFold = false, componentOrderIdx } = additionalData || {};
    const { componentType = "", id = "", serverFptiData, ...componentProps } = componentData;
    const Component = ComponentsMap[componentType];
    if (injectComponentContext) {
      componentProps.componentContext = getComponentContextProps({ componentOrderIdx });
    }
    if (!Component) {
      return /* @__PURE__ */ jsx(
        "div",
        {
          "data-missed": componentType
        },
        `${componentType}-${id}`
      );
    }
    const LoadedComponent = /* @__PURE__ */ jsx(Component, { ...componentProps });
    if (isPreviewEnabled && ErrorFallback) {
      const ErrorComponent = useCallback(
        ({ error }) => /* @__PURE__ */ jsx(
          ErrorFallback,
          {
            context: pageContext,
            error,
            componentType,
            id
          }
        ),
        [pageContext, componentType, id]
      );
      return /* @__PURE__ */ jsx(
        ErrorBoundaryComponent,
        {
          fallbackComponent: ErrorComponent,
          children: LoadedComponent
        },
        `error-boundary-${componentType}-${id}`
      );
    }
    const [Wrapper, wrapperProps] = getComponentWrapper(pageContext, ComponentsMap, { isAboveTheFold });
    return /* @__PURE__ */ jsx(
      Suspense,
      {
        fallback: null,
        children: serverFptiData ? /* @__PURE__ */ jsxs(FptiWrapper, { onVisible: () => triggerFpti({ ...serverFptiData }), children: [
          " ",
          /* @__PURE__ */ jsx(Wrapper, { ...wrapperProps, children: LoadedComponent })
        ] }) : /* @__PURE__ */ jsx(Wrapper, { ...wrapperProps, children: LoadedComponent })
      },
      `suspense-${componentType}-${id}`
    );
  };
};
const injectImageLoadHint = (component) => {
  Object.entries(component).forEach(([key, value]) => {
    if (key === "componentType" && value === "Image") {
      component.loading = "eager";
    } else if (typeof component[key] === "object" && component[key] !== null) {
      injectImageLoadHint(component[key]);
    }
  });
};
const renderCMSPageComponents = ({
  customRenderFn,
  componentSlots,
  renderCmsComponent,
  header,
  footer,
  pageContext
}) => {
  var _a, _b, _c;
  let componentOrderIdxCounter = 0;
  const { __EXPERIMENTAL__: { aboveTheFoldComponentCount = 3, disableAnimationsAboveTheFold = false } = {} } = (pageContext == null ? void 0 : pageContext.additionalContext) || {};
  const aboveHeaderComponents = (_a = componentSlots.aboveHeaderComponents) == null ? void 0 : _a.map((aComp) => {
    const renderedComp = renderCmsComponent(aComp, {
      isAboveTheFold: componentOrderIdxCounter < aboveTheFoldComponentCount,
      componentOrderIdx: componentOrderIdxCounter
    });
    componentOrderIdxCounter += 1;
    return renderedComp;
  });
  const bodyComponents = (_b = componentSlots.bodyComponents) == null ? void 0 : _b.map((aComp) => {
    if (disableAnimationsAboveTheFold && componentOrderIdxCounter < aboveTheFoldComponentCount) {
      injectImageLoadHint(aComp);
    }
    const renderedComp = renderCmsComponent(aComp, {
      isAboveTheFold: componentOrderIdxCounter < aboveTheFoldComponentCount,
      componentOrderIdx: componentOrderIdxCounter
    });
    componentOrderIdxCounter += 1;
    return renderedComp;
  });
  const belowFooterComponents = (_c = componentSlots.belowFooterComponents) == null ? void 0 : _c.map((aComp) => {
    const renderedComp = renderCmsComponent(aComp, { componentOrderIdx: componentOrderIdxCounter });
    componentOrderIdxCounter += 1;
    return renderedComp;
  });
  return customRenderFn ? customRenderFn({
    aboveHeader: aboveHeaderComponents,
    body: bodyComponents,
    belowFooter: belowFooterComponents
  }) : /* @__PURE__ */ jsxs(Fragment, { children: [
    aboveHeaderComponents,
    header,
    bodyComponents,
    footer,
    belowFooterComponents
  ] });
};
const getComponentsInDocument = (pageModel) => {
  const aboveHeaderComponents = [];
  const belowFooterComponents = [];
  const bodyComponents = [];
  const { id, componentType, ...componentsGroup } = pageModel.pageReference || pageModel;
  const layout = {};
  Object.entries(componentsGroup).forEach(([key, group]) => {
    layout[key] = Array.isArray(group) ? group : [group];
  });
  const buckets = Object.entries(layout);
  buckets.forEach(([key, components]) => {
    const componentsFiltered = components.filter(Boolean);
    switch (key) {
      case "aboveHeader":
        aboveHeaderComponents.push(...componentsFiltered);
        break;
      case "belowFooter":
        belowFooterComponents.push(...componentsFiltered);
        break;
      default:
        bodyComponents.push(...componentsFiltered);
    }
  });
  return {
    aboveHeaderComponents,
    bodyComponents,
    belowFooterComponents
  };
};
const generateComponentSlots = ({ pageModel, layout, renderType }) => {
  const slots = {
    aboveHeaderComponents: [],
    bodyComponents: [],
    belowFooterComponents: []
  };
  if (renderType === "component") {
    slots.bodyComponents = [pageModel];
  } else {
    const layoutComponents = layout(pageModel);
    Object.keys(layoutComponents).forEach((key) => {
      slots[key] = layoutComponents[key];
    });
  }
  return slots;
};
function CMSPage({
  pageModel,
  componentKit: ComponentsMap,
  dir = "ltr",
  pageContext,
  header,
  footer,
  render,
  hostedBy = "cms-engine",
  previewTransformHelpers,
  previewCustomResolvers,
  previewCustomValidations,
  layout = getComponentsInDocument
}) {
  const { additionalContext: { preview = {}, contentSource = -1 } = {}, ...parentContextProps } = pageContext;
  const renderType = ComponentsMap[pageModel.componentType || ""] ? "component" : "page";
  let PreviewPanel = null;
  let PreviewBanner = null;
  if (preview == null ? void 0 : preview.enabled) {
    PreviewPanel = lazy(() => import("./PreviewPanel-a9b49da9.js"));
    PreviewBanner = lazy(() => import("./PreviewBanner-a5caed26.js"));
    preview.type = renderType;
  }
  const componentSlots = generateComponentSlots({ pageModel, layout, renderType });
  const renderCmsComponent = initializeComponentRender(ComponentsMap, pageContext);
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: "document-body-root",
      "data-hosted-by": hostedBy,
      dir,
      "data-source": contentSource,
      children: /* @__PURE__ */ jsxs(ComponentsMap.Parent, { ...parentContextProps, children: [
        (preview == null ? void 0 : preview.enabled) && PreviewBanner && /* @__PURE__ */ jsx(ForceClientSideRender, { children: /* @__PURE__ */ jsx(PreviewBanner, {}) }),
        renderCMSPageComponents({
          customRenderFn: render,
          componentSlots,
          renderCmsComponent,
          header,
          footer,
          pageContext
        }),
        (preview == null ? void 0 : preview.enabled) && PreviewPanel && /* @__PURE__ */ jsx(ForceClientSideRender, { children: /* @__PURE__ */ jsx(
          PreviewPanel,
          {
            content: pageModel,
            context: pageContext,
            previewCustomResolvers: previewCustomResolvers || {},
            previewTransformHelpers,
            previewCustomValidations: previewCustomValidations || [],
            layout
          }
        ) })
      ] })
    }
  );
}
function EmotionCMSPage({
  pageModel,
  componentKit: ComponentsMap,
  dir = "ltr",
  theme = {},
  pageContext,
  header,
  footer,
  render,
  hostedBy = "cms-engine",
  emotionCache,
  previewTransformHelpers,
  previewCustomResolvers,
  previewCustomValidations = [],
  layout = getComponentsInDocument
}) {
  const {
    additionalContext: { preview, contentSource = -1 },
    ...parentContextProps
  } = pageContext;
  const renderType = ComponentsMap[(pageModel == null ? void 0 : pageModel.componentType) ?? ""] ? "component" : "page";
  let PreviewPanel;
  let PreviewBanner;
  if (preview == null ? void 0 : preview.enabled) {
    preview.type = renderType;
    PreviewPanel = lazy(() => import("./PreviewPanel-a9b49da9.js"));
    PreviewBanner = lazy(() => import("./PreviewBanner-a5caed26.js"));
  }
  const componentSlots = generateComponentSlots({ pageModel, layout, renderType });
  const renderCmsComponent = initializeComponentRender(ComponentsMap, pageContext, { injectComponentContext: true });
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: "document-body-root",
      "data-hosted-by": hostedBy,
      dir,
      "data-source": contentSource,
      children: /* @__PURE__ */ jsx(CacheProvider, { value: emotionCache, children: /* @__PURE__ */ jsx(ThemeProvider, { theme, children: /* @__PURE__ */ jsxs(ComponentsMap.Parent, { ...parentContextProps, children: [
        (preview == null ? void 0 : preview.enabled) && PreviewBanner && /* @__PURE__ */ jsx(ForceClientSideRender, { children: /* @__PURE__ */ jsx(PreviewBanner, {}) }),
        renderCMSPageComponents({
          customRenderFn: render,
          componentSlots,
          renderCmsComponent,
          header,
          footer,
          pageContext
        }),
        (preview == null ? void 0 : preview.enabled) && PreviewPanel && /* @__PURE__ */ jsx(ForceClientSideRender, { children: /* @__PURE__ */ jsx(
          PreviewPanel,
          {
            content: pageModel,
            context: pageContext,
            previewTransformHelpers,
            previewCustomResolvers: previewCustomResolvers || {},
            previewCustomValidations: previewCustomValidations || [],
            layout
          }
        ) })
      ] }) }) })
    }
  );
}
export {
  CMSPage,
  EmotionCMSPage,
  FptiWrapper,
  triggerFpti
};
